import { DataSource } from 'hooks/useHttpClient';
import {
    Address,
    AddressResolver,
    FactAudit,
    PaginationMeta,
    PaginationMetaResolver,
    LegacyAddress,
    LegacyAddressResolver
} from './Common';
import { Member, MemberListResolver } from './Members';
import { BasicMappedStatus } from './Candidate';

export type Partner = {
    id: number;
    name: string;
    configuredAvatar: string;
    configuredShortAvatar: string;
    shortDescription?: string;
};

export type CreateCompany = {
    avatar?: string | null;
    name: string;
    status: 'Active' | 'Inactive';
    website: string | null;
    priority: 'High' | 'Medium' | 'Low'; // DEPRECATED
};

export type Company = {
    id: number;
    avatar: string | null;
    name: string;
    bussinesTechObjetives: string;
    legalNotes: string;
    location: Address | null;
    legacyLocation?: LegacyAddress | null;
    NDADocuments: string;
    website: string | null;
    status: 'Active' | 'Inactive';
    tags: string | null;
    totalCandidates: number;
    activeJobs: number;
    totalHeadcount: number; // TODO Map this from response
    totalHires: number; // TODO Map this from response
    updatedAudit?: FactAudit;
    createdAudit?: FactAudit;
};

export type PaginatedCompanyList = {
    data: Company[];
    meta: PaginationMeta;
};

export const CompanyStatuses: (BasicMappedStatus & {
    color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
})[] = [
    { id: 1, name: 'Active', color: 'primary' },
    { id: 2, name: 'Inactive', color: 'error' }
];

export function CompanyNameAvailableResolver(dataSource: DataSource, apiResponse: any): boolean {
    try {
        switch (dataSource) {
            case 'hyrerocket':
                return apiResponse.available;

            default:
                return false;
        }
    } catch (error) {
        return false;
    }
}

export function CreateOrSingleCompanyResolver(dataSource: DataSource, apiResponse: any): Company | null {
    try {
        console.log(apiResponse);
        let company = null;
        switch (dataSource) {
            case 'hyrerocket': {
                company = apiResponse.data;
                const [headCounts, hires] = company.job_requisitions.reduce(
                    (acc: number[], job: any) => {
                        if (!['On Hold', 'Canceled'].some((status) => status === job.status)) {
                            acc[0] += job.headcount;
                            acc[1] +=
                                job.job_requisition_candidates?.filter((candidate: any) => [13, 14].some((a) => a === +candidate.stage_id))
                                    ?.length || 0;
                        }

                        return acc;
                    },
                    [0, 0]
                ) as number[];

                return {
                    id: company.id,
                    avatar: company.company_logo,
                    name: company.name,
                    bussinesTechObjetives: company.bussines_tech_objetives,
                    legalNotes: company.legal_notes,
                    location: AddressResolver(dataSource, company.location),
                    legacyLocation: LegacyAddressResolver(dataSource, company.location),
                    NDADocuments: company.nda_doc,
                    website: company.website,
                    status: company.status,
                    tags: company.tags,
                    totalCandidates: company.total_candidates,
                    activeJobs: company.active_jobs,
                    totalHeadcount: headCounts,
                    totalHires: hires,
                    createdAudit: {
                        userId: company.created_by?.id || 0,
                        userFullName: `${company.created_by?.first_name} ${company.created_by?.last_name}`,
                        dateStr: company.created_at
                    },
                    updatedAudit: {
                        userId: company.last_update_by?.id || 0,
                        userFullName: `${company.last_update_by?.first_name} ${company.last_update_by?.last_name}`,
                        dateStr: company.updated_at
                    }
                } as Company;
            }

            default:
                return null;
        }
    } catch (error) {
        console.log(error);
        return null;
    }
}

// TODO Refactor PaginatedList generic type
export function CompanyListResolver(dataSource: DataSource, apiResponse: any): PaginatedCompanyList | null {
    try {
        switch (dataSource) {
            case 'hyrerocket':
                return {
                    data: apiResponse.data.map((company: any) => {
                        const [headCounts, hires] = company.job_requisitions.reduce(
                            (acc: number[], job: any) => {
                                if (!['On Hold', 'Canceled'].some((status) => status === job.status)) {
                                    acc[0] += job.headcount;
                                    acc[1] += job.job_requisition_candidates.filter((candidate: any) =>
                                        [13, 14].some((a) => a === +candidate.stage_id)
                                    ).length;
                                }

                                return acc;
                            },
                            [0, 0]
                        ) as number[];

                        return {
                            id: company.id,
                            avatar: company.company_logo,
                            name: company.name,
                            bussinesTechObjetives: company.bussines_tech_objetives,
                            legalNotes: company.legal_notes,
                            location: AddressResolver(dataSource, company.location),
                            legacyLocation: LegacyAddressResolver(dataSource, company.location),
                            NDADocuments: company.nda_doc,
                            website: company.website,
                            status: company.status,
                            tags: company.tags,
                            totalCandidates: company.total_candidates,
                            activeJobs: company.active_jobs,
                            totalHeadcount: headCounts,
                            totalHires: hires
                        };
                    }) as Company[],
                    meta: PaginationMetaResolver(dataSource, apiResponse.meta) as PaginationMeta
                };

            default:
                return null;
        }
    } catch (error) {
        console.log(error);
        return null;
    }
}

export function QuickCompanySingleResolver(dataSource: DataSource, apiResponse: any): Company | null {
    try {
        switch (dataSource) {
            case 'hyrerocket':
                return {
                    id: apiResponse.id,
                    avatar: apiResponse.company_logo,
                    name: apiResponse.name,
                    bussinesTechObjetives: apiResponse.bussines_tech_objetives,
                    legalNotes: apiResponse.legal_notes,
                    location: null,
                    NDADocuments: '',
                    website: apiResponse.website,
                    status: apiResponse.status,
                    tags: null,
                    totalCandidates: 0,
                    activeJobs: 0,
                    totalHeadcount: 0,
                    totalHires: 0
                } as Company;

            default:
                return null;
        }
    } catch (error) {
        return null;
    }
}

export function CompanyMemberListResolver(dataSource: DataSource, apiResponse: any): Member[] {
    try {
        switch (dataSource) {
            case 'hyrerocket':
                return MemberListResolver(dataSource, apiResponse.data.user);

            default:
                return [];
        }
    } catch (error) {
        console.log(error);
        return [];
    }
}

export function PartnerSingleResolver(dataSource: DataSource, apiResponse: any): Partner | null {
    try {
        switch (dataSource) {
            case 'hyrerocket':
                return {
                    id: apiResponse.id,
                    name: apiResponse.name,
                    configuredAvatar: apiResponse.configured_avatar,
                    configuredShortAvatar: apiResponse.configured_short_avatar,
                    shortDescription: apiResponse.short_dsc
                } as Partner;

            default:
                return null;
        }
    } catch (error) {
        return null;
    }
}
